import type { FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type { RoomLayoutInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { EntityInstance, RoomLayoutInstance } from "building-types/roomLayouts/roomLayoutTypes";
import type { RelatedRoomInfo } from "building-types/rooms/relatedRoomsMapTypes";
import type { Table } from "common/data/table";
import { evaluateEntityForComponentInstance } from "doc/formula/evalFormulas";

interface ComponentEntityInstance {
  readonly parentId: string;
  readonly mirrored: boolean;
  readonly instanceId: string;
  readonly componentInstanceChain: readonly string[];
}

/**
 * Returns whether a placement's room layout instance is enabled for the given component instance chain.
 * Non-room-layout-instance placements always return true.
 * Use an empty chain to indicate a main-world / archetype placement.
 */
export function isRoomLayoutInstanceEnabledForChain(
  handleOverride: RoomLayoutInstanceHandle | undefined,
  componentInstanceChain: readonly string[],
  roomLayoutInstances: Table<RoomLayoutInstance>,
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
): boolean {
  if (!handleOverride || handleOverride.type !== "ROOM_LAYOUT_INSTANCE") {
    return true;
  }
  const roomLayoutInstance = roomLayoutInstances[handleOverride.id];
  if (!roomLayoutInstance) {
    return false;
  }
  const evaluated = evaluateEntityForComponentInstance(
    roomLayoutInstance,
    componentInstanceChain,
    definitions,
    optionChoices,
  );
  return !evaluated.disabled;
}

/**
 * Returns component instances for a given entity, similar to entityInstancesForEntity but for component instances.
 * If an entity is in a room that participates in components, it could have multiple instances.
 */
function componentInstancesForEntity(
  relatedRoomsMap: { readonly [roomId: string]: ReadonlyArray<RelatedRoomInfo> },
  sourceRoomId: string,
): ComponentEntityInstance[] {
  const componentInstances: ComponentEntityInstance[] = [];
  const relatedRooms = relatedRoomsMap[sourceRoomId] || [];

  for (const { roomId: relatedRoomId, mirrored, componentInstanceChain } of relatedRooms) {
    if (relatedRoomId === sourceRoomId) {
      continue; // Skip self
    }

    const instanceId = componentInstanceChain?.[0];
    if (!instanceId) {
      continue;
    }

    componentInstances.push({
      parentId: relatedRoomId,
      mirrored,
      instanceId,
      componentInstanceChain,
    });
  }

  return componentInstances;
}

// Creates copies of the entity instances and adds them to allInstances.
export function copyEntityInstancesForComponents(
  relatedRoomsMap: { readonly [roomId: string]: ReadonlyArray<RelatedRoomInfo> },
  entityInstances: EntityInstance[],
  allInstances: EntityInstance[],
) {
  for (const entityInstance of entityInstances) {
    if (entityInstance.parentId) {
      const componentInstances = componentInstancesForEntity(relatedRoomsMap, entityInstance.parentId);
      for (const componentInstance of componentInstances) {
        allInstances.push({
          ...entityInstance,
          ...componentInstance,
        });
      }
    }
  }
}

/**
 * Populates allInstances and evaluatedEntitiesByInstanceId with the archetypes from entityInstances and the component instances
 * Everything is filtered by if the room layout instance (archetype or component instance) is enabled
 * The component instances are filtered by if the entity itself is enabled (accounting for instance options)
 * Does not filter the archetypes by if the entity itself is enabled, callers must do this after
 *
 * `includeDisabledInstances` keeps disabled component instances instead of dropping them — geometry
 * generation doesn't need them, but hotspots do, since the anchor room still exists in every unit.
 */
export function copyEntityForComponentsWithOptions<T extends FormulaDisabledDocEntityBase>(
  entity: T,
  relatedRoomsMap: { readonly [roomId: string]: ReadonlyArray<RelatedRoomInfo> },
  entityInstances: EntityInstance[],
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
  allInstances: EntityInstance[],
  evaluatedEntitiesByInstanceId: Map<string, T>,
  roomLayoutInstances: Table<RoomLayoutInstance> = {},
  includeDisabledInstances = false,
) {
  for (const entityInstance of entityInstances) {
    if (
      isRoomLayoutInstanceEnabledForChain(
        entityInstance.handleOverride,
        [],
        roomLayoutInstances,
        definitions,
        optionChoices,
      )
    ) {
      allInstances.push(entityInstance);
    }
    if (entityInstance.parentId) {
      const componentInstances = componentInstancesForEntity(relatedRoomsMap, entityInstance.parentId);
      for (const componentInstance of componentInstances) {
        if (
          !isRoomLayoutInstanceEnabledForChain(
            entityInstance.handleOverride,
            componentInstance.componentInstanceChain,
            roomLayoutInstances,
            definitions,
            optionChoices,
          )
        ) {
          continue;
        }
        const evaluatedEntity = evaluateEntityForComponentInstance(
          entity,
          componentInstance.componentInstanceChain,
          definitions,
          optionChoices,
        );
        if (evaluatedEntity.disabled && !includeDisabledInstances) {
          continue;
        }
        evaluatedEntitiesByInstanceId.set(componentInstance.instanceId, evaluatedEntity);
        allInstances.push({
          ...entityInstance,
          ...componentInstance,
        });
      }
    }
  }
}
